import { styled, Theme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';

import { generatePath, Link } from 'react-router-dom';
import EmptyState from '@src/components/utilities/EmptyState/EmptyState';

import HospitalUserDTO from '@src/adapters/dto/hospital/HospitalUserDTO';
import StatusTableCell from './StatusTableCell';
import RoleTableCell from './RoleTableCell';
import { Typography } from '@mui/material';
import { Routes } from '@src/navigation/Routes';
import { StatusUpdate } from '@src/constants/StatusUpdate';
import { useNavigationStore } from '@src/adapters/stores/useNavigationStore';
import { AccessStatus } from '@src/constants/UserStatus';

const PREFIX = 'HospitalUsersTable';

const classes = {
  root: `${PREFIX}-root`,
  table: `${PREFIX}-table`,
  tableHead: `${PREFIX}-tableCell`,
  header: `${PREFIX}-header`,
  link: `${PREFIX}-link`,
};

const Root = styled('div')(({ theme }: { theme: Theme }) => ({
  [`&.${classes.root}`]: {
    width: '100%',
  },

  [`& .${classes.tableHead}`]: {
    color: theme.palette.primary,
    paddingLeft: '16px',
  },

  [`& .${classes.link}`]: {
    textDecoration: 'none',
    '&:visited': {
      color: 'inherit',
    },
  },
}));

interface Props {
  users: HospitalUserDTO[];
  selectedPage: number;
  rowsPerPage: number;
  recordCount: number;
  changePage(value: number): void;
  changeRowsPerPage(value: number): void;
  onUserStatusChange?: (userId: string, status: StatusUpdate) => void;
}

const REQUESTED_ACCESS: keyof typeof AccessStatus = 'Requested';

function HospitalUsersTable({
  users,
  selectedPage,
  rowsPerPage,
  recordCount,
  changePage: setPage,
  changeRowsPerPage: setRowsPerPage,
  onUserStatusChange,
}: Props) {
  const { currentHospital, currentOrganisation, setNavigatedUser } =
    useNavigationStore();
  const isEmpty = !users || users.length === 0;

  return (
    <Root className={classes.root}>
      <TableContainer>
        <Table
          className={classes.table}
          aria-labelledby="tableTitle"
          size="small"
          aria-label="Contents table">
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell>User Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isEmpty ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <EmptyState title="No users to show!" />
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => {
                const isRequestedAccess = user.status === REQUESTED_ACCESS;

                return (
                  <TableRow key={user.userId}>
                    <TableCell>
                      {isRequestedAccess ? (
                        <Typography
                          variant="subtitle2"
                          color="primary"
                          sx={{ cursor: 'default' }}>
                          {user.firstName} {user.lastName}
                        </Typography>
                      ) : (
                        <Link
                          to={generatePath(Routes.OrganisationHospitalUser, {
                            organisationSlug: currentOrganisation?.name ?? '',
                            hospitalSlug: currentHospital?.name ?? '',
                            userSlug: user.userId,
                          })}
                          onClick={() => {
                            setNavigatedUser({
                              id: user.userId ?? '',
                              name: `${user.firstName} ${user.lastName}`,
                            });
                          }}
                          className={classes.link}>
                          <Typography variant="subtitle2" color="primary">
                            {user.firstName} {user.lastName}
                          </Typography>
                        </Link>
                      )}
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <RoleTableCell roles={user.roles} />
                    <StatusTableCell
                      user={user}
                      onStatusChange={onUserStatusChange}
                      isRequestedAccess={isRequestedAccess}
                    />
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {!isEmpty && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={recordCount}
          rowsPerPage={rowsPerPage}
          page={selectedPage}
          onPageChange={(event, number) => setPage(number)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      )}
    </Root>
  );
}

export default HospitalUsersTable;
